<template>
    <AccordionBasic
        title="asset_class"
        class="mb-2"
        :open="true"
    >
        <template
            v-for="field in fields"
            :key="field.id"
        >
            <NumberField
                v-if="
                    field.type === 'number' &&
                    !!asset.asset_class &&
                    field.appliedTo.includes(asset.asset_class)
                "
                :name="field.id"
                :label="field.label"
                :required="field.required ?? false"
                :min="field.validation?.min"
                :max="field.validation?.max"
                :model-value="getFieldValue(field)"
                @update:model-value="setFieldValue(field.id, $event)"
            />
            <TextField
                v-if="
                    field.type === 'text' &&
                    !!asset.asset_class &&
                    field.appliedTo.includes(asset.asset_class)
                "
                :name="field.id"
                :label="field.label"
                :required="field.required ?? false"
                :model-value="getFieldValue(field)"
                @update:model-value="setFieldValue(field.id, $event)"
            />
            <ToggleSwitch
                v-if="
                    field.type === 'toggle' &&
                    !!asset.asset_class &&
                    field.appliedTo.includes(asset.asset_class)
                "
                :name="field.id"
                :label="field.label"
                :required="field.required ?? false"
                :model-value="getFieldValue(field)"
                @update:model-value="setFieldValue(field.id, $event)"
            />
            <SelectField
                v-if="
                    field.type === 'select' &&
                    !!asset.asset_class &&
                    field.appliedTo.includes(asset.asset_class)
                "
                :name="field.id"
                :label="field.label"
                :required="field.required ?? false"
                :options="field.options ?? []"
                :model-value="getFieldValue(field)"
                @update:model-value="setFieldValue(field.id, $event)"
            />
            <SelectField
                v-if="
                    field.type === 'multiselect' &&
                    !!asset.asset_class &&
                    field.appliedTo.includes(asset.asset_class)
                "
                :name="field.id"
                :label="field.label"
                :required="field.required ?? false"
                :options="field.options ?? []"
                :multiple="true"
                :model-value="getFieldValue(field)"
                @update:model-value="setFieldValue(field.id, $event)"
            />
        </template>
    </AccordionBasic>
</template>

<script setup lang="ts">
import AccordionBasic from '@src/components/AccordionBasic.vue'
import NumberField from '@src/components/NumberField.vue'
import SelectField from '@src/components/SelectField.vue'
import TextField from '@src/components/TextField.vue'
import ToggleSwitch from '@src/components/ToggleSwitch.vue'
import { useEnum } from '@src/composables/common'
import {
    AssetClass,
    AssetJurisdiction,
    AssetRegulation,
    SpvLegalForm,
    TransferRestriction,
} from '@src/enums'
import { Asset } from '@src/types'
import { normalizeAssetClassToggles } from '@src/utils/helpers/normalizeAssetClassToggles'
import { watch } from 'vue'

const props = defineProps<{
    asset: Asset
}>()

const { mapEnumToArray } = useEnum()

function getFieldValue(field: { id: string; type: string }): any {
    const value = props.asset[field.id as keyof Asset]
    if (value !== undefined && value !== null) {
        return value
    }
    if (field.type === 'toggle') {
        return false
    }
    if (field.type === 'multiselect') {
        return []
    }
    return null
}

function setFieldValue(id: string, value: unknown) {
    ;(props.asset as any)[id] = value
}

const fields = [
    {
        id: 'asset_regulation',
        label: 'asset_regulation',
        type: 'select',
        options: mapEnumToArray(AssetRegulation),
        required: true,
        appliedTo: Object.values(AssetClass),
    },
    {
        id: 'asset_jurisdiction',
        label: 'asset_jurisdiction',
        type: 'multiselect',
        options: Object.values(AssetJurisdiction),
        required: true,
        appliedTo: Object.values(AssetClass),
    },
    {
        id: 'is_subordinated',
        label: 'is_subordinated',
        type: 'toggle',
        required: true,
        appliedTo: [AssetClass.SUBORDINATED_LOAN, AssetClass.BOND],
    },
    {
        id: 'is_tied_agent',
        label: 'is_tied_agent',
        type: 'toggle',
        required: false,
        appliedTo: Object.values(AssetClass),
    },
    {
        id: 'transferable',
        label: 'transferable',
        type: 'toggle',
        required: true,
        appliedTo: Object.values(AssetClass),
    },
    // Commented out for now as we need clarification on the stock type and class
    // {
    //     id: 'stock_type',
    //     label: 'stock_type',
    //     type: 'select',
    //     options: [],
    //     required: true,
    //     appliedTo: [AssetClass.SHARE, AssetClass.PARTICIPATION_CERT],
    // },
    // {
    //     id: 'stock_class',
    //     label: 'stock_class',
    //     type: 'select',
    //     required: true,
    //     options: [],
    //     appliedTo: [AssetClass.SHARE, AssetClass.PARTICIPATION_CERT],
    // },
    {
        id: 'transfer_restriction',
        label: 'transfer_restriction',
        type: 'select',
        options: mapEnumToArray(TransferRestriction),
        required: false,
        appliedTo: [AssetClass.SHARE, AssetClass.PARTICIPATION_CERT],
    },
    {
        id: 'pre_money_valuation',
        label: 'pre_money_valuation',
        type: 'number',
        validation: {
            min: 0,
        },
        required: true,
        appliedTo: [
            AssetClass.SHARE,
            AssetClass.PARTICIPATION_CERT,
            AssetClass.PROFIT_PARTICIPATION,
            AssetClass.CONVERTIBLE_BOND,
        ],
    },
    {
        id: 'equity_percentage',
        label: 'equity_percentage',
        type: 'number',
        validation: {
            min: 0,
            max: 100,
        },
        appliedTo: [AssetClass.SHARE, AssetClass.PARTICIPATION_CERT],
    },
    {
        id: 'dividend_preference_pct',
        label: 'dividend_preference_pct',
        type: 'number',
        validation: {
            min: 0,
            max: 100,
        },
        appliedTo: [AssetClass.SHARE, AssetClass.PARTICIPATION_CERT],
    },
    {
        id: 'cooperative_registry_number',
        label: 'cooperative_registry_number',
        type: 'text',
        required: true,
        appliedTo: [AssetClass.COOPERATIVE_SHARE],
    },
    {
        id: 'cooperative_statute_url',
        label: 'cooperative_statute_url',
        type: 'text',
        required: true,
        appliedTo: [AssetClass.COOPERATIVE_SHARE],
    },
    {
        id: 'cooperative_notice_period',
        label: 'cooperative_notice_period',
        type: 'text',
        required: true,
        appliedTo: [AssetClass.COOPERATIVE_SHARE],
    },
    {
        id: 'cooperative_additional_contribution',
        label: 'cooperative_additional_contribution',
        type: 'toggle',
        required: true,
        appliedTo: [AssetClass.COOPERATIVE_SHARE],
    },
    {
        id: 'cooperative_mandatory_shares',
        label: 'cooperative_mandatory_shares',
        type: 'number',
        validation: {
            min: 1,
        },
        required: true,
        appliedTo: [AssetClass.COOPERATIVE_SHARE],
    },
    {
        id: 'spv_legal_form',
        label: 'spv_legal_form',
        type: 'select',
        options: mapEnumToArray(SpvLegalForm),
        required: true,
        appliedTo: [AssetClass.PROFIT_PARTICIPATION_SPV],
    },
    {
        id: 'spv_participation_quota_pct',
        label: 'spv_participation_quota_pct',
        type: 'number',
        validation: {
            min: 0,
            max: 100,
        },
        required: true,
        appliedTo: [AssetClass.PROFIT_PARTICIPATION_SPV],
    },
    {
        id: 'spv_participation_exemption',
        label: 'spv_participation_exemption',
        type: 'toggle',
        required: true,
        appliedTo: [AssetClass.PROFIT_PARTICIPATION_SPV],
    },
    {
        id: 'spv_corporate_tax_rate',
        label: 'spv_corporate_tax_rate',
        type: 'number',
        validation: {
            min: 0,
            max: 100,
        },
        required: true,
        appliedTo: [AssetClass.PROFIT_PARTICIPATION_SPV],
    },
    {
        id: 'spv_lead_investor',
        label: 'spv_lead_investor',
        type: 'text',
        required: true,
        appliedTo: [AssetClass.PROFIT_PARTICIPATION_SPV],
    },
]

// Fields applied to every class (asset_regulation, asset_jurisdiction, transferable,
// is_tied_agent) are never reset here - they aren't derived from asset_class the way
// the class-specific fields below are, and some of them are required server-side, so
// nulling them just because the class switched (or was cleared) would trade one
// "prohibited" error for a new "required"/type error.
const allClasses = Object.values(AssetClass)

watch(
    () => props.asset.asset_class,
    () => {
        const currentAsset = props.asset
        fields
            .filter(
                (field) =>
                    field.type !== 'toggle' &&
                    field.appliedTo.length < allClasses.length
            )
            .forEach((field) => {
                const applies =
                    !!currentAsset.asset_class &&
                    field.appliedTo.includes(currentAsset.asset_class)
                // Not applicable to the current class: must stay null,
                // otherwise the backend's "prohibited unless" rules reject the save.
                if (
                    !applies &&
                    currentAsset[field.id as keyof Asset] !== null
                ) {
                    ;(currentAsset as any)[field.id] = null
                }
            })
        normalizeAssetClassToggles(currentAsset)
    },
    { immediate: true }
)
</script>
